import React, {
    LegacyRef,
    memo,
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import CSS from "csstype"
import {useModalWrapperContext} from "../../modals/modalWrapper"
import {PortalBuilder} from "../../features/PortalBuilder"
import RRM from "react-responsive-modal"
import classNames from "classnames"
import styles from "./Modal.module.scss"
import {ContainerLayout} from "../ContainerLayout/ContainerLayout"
import {SmoothComponent} from "../SmoothComponent/SmoothComponent"
import {Scrollbars as CustomScrollbars} from "react-custom-scrollbars"
import {Scrollbars} from "../Scrollbars"
import {DisableFormElementsComponent} from "../../features/DisableFormElementsComponent"
import {
    ModalLoaderContext,
    ModalPositionContext,
    ModalScrollViewContext,
    ModalTopStickyContext,
} from "../../context/modalContexts"

export interface ModalProps {
    id?: string
    className?: string
    darkTheme?: boolean
    disableFormElements?: boolean

    open?: boolean
    onClose: () => void

    showCloseIcon?: boolean
    smoothCloseIcon?: boolean
    closeIconClass?: string
    customCloseIcon?: React.ReactNode

    right?: boolean
    bottom?: boolean
    wide?: boolean
    ultraWide?: boolean
    fullScreen?: boolean
    center?: boolean
    /**
     * Use for multiple-column modals, where each column is free-scrollable
     */
    ownScroll?: boolean
    noBottomPadding?: boolean

    focusTrapped?: boolean
    closeOnEsc?: boolean
    returnFocusOnDeactivate?: boolean
    withoutHeader?: boolean
    scrollDisabled?: boolean
    noBackground?: boolean
    noShadow?: boolean
    noScrollWrapper?: boolean

    overlayClassName?: string
    "data-cy"?: string
    "data-test"?: string
    animation?: "left" | "right" | "opacity" | "immediately"
    customWrapperStyles?: CSS.Properties
    containerClassName?: string
    rootClassName?: string
    children?: ReactNode
    useStickyHeader?: boolean
}

const JustComponent: React.FC<{show: boolean; children?: ReactNode}> = ({children, show}) =>
    show ? <>{children}</> : null

interface CloseIconProps extends Pick<ModalProps, "customCloseIcon"> {
    className?: string
    onClick: () => void
    smooth?: boolean
    show: boolean
    isSmallIcon?: boolean
    isSmallestIcon?: boolean
}

export const CloseIcon: React.FC<CloseIconProps> = ({
    className,
    onClick,
    smooth,
    show,
    customCloseIcon,
    isSmallestIcon,
    isSmallIcon = true,
}) => {
    const Wrapper = smooth ? SmoothComponent : JustComponent
    return (
        <div
            data-cy={"close-modal-icon"}
            data-test={"close-modal"}
            onClick={onClick}
            className={classNames(className, styles.focusButton)}
        >
            <Wrapper show={show}>
                {customCloseIcon || (
                    <div>
                        <svg
                            className={classNames(
                                styles.icon,
                                isSmallIcon && styles.isSmallIcon,
                                isSmallestIcon && styles.isSmallestIcon
                            )}
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M0.302551 1.76497C-0.10085 1.36121 -0.10085 0.706581 0.302551 0.30282C0.705951 -0.10094 1.35999 -0.10094 1.76339 0.30282L23.676 22.235C24.0794 22.6388 24.0794 23.2934 23.676 23.6972C23.2726 24.1009 22.6186 24.1009 22.2152 23.6972L0.302551 1.76497Z" />
                            <path d="M1.7848 23.6972C1.3814 24.1009 0.727358 24.1009 0.323957 23.6972C-0.0794439 23.2934 -0.0794439 22.6388 0.323957 22.235L22.2366 0.302844C22.64 -0.100917 23.294 -0.100917 23.6974 0.302844C24.1008 0.706604 24.1009 1.36123 23.6974 1.76499L1.7848 23.6972Z" />
                        </svg>
                    </div>
                )}
            </Wrapper>
        </div>
    )
}

export const Modal: React.FC<ModalProps> = ({
    id,
    open,
    onClose,
    className,
    children,
    showCloseIcon = false,
    smoothCloseIcon = false,
    right,
    bottom,
    wide,
    ultraWide,
    fullScreen,
    darkTheme,
    ownScroll,
    noBottomPadding,
    focusTrapped = true,
    closeOnEsc = true,
    withoutHeader,
    scrollDisabled = true,
    noBackground,
    overlayClassName,
    animation,
    customWrapperStyles,
    disableFormElements,
    center,
    closeIconClass,
    customCloseIcon,
    noShadow,
    containerClassName,
    rootClassName,
    noScrollWrapper,
    ...props
}) => {
    const scrollViewRef = useRef<HTMLDivElement | null>(null)
    const [scrollInfo] = useState(() => {
        const {scrollTop, scrollLeft} = document.documentElement
        return {
            scrollTop,
            scrollLeft,
        }
    })
    const [dataTestOpen, setDataTestOpen] = useState(false)

    const handleChangeResize = useCallback((isOverflowHidden: boolean) => {
        if (!isOverflowHidden) {
            document.body.style.overflow = "hidden"
        }
    }, [])

    useEffect(() => {
        const handler = () => {
            handleChangeResize(document.body.style.overflow === "hidden")
        }

        if (scrollDisabled) {
            window.addEventListener("resize", handler)
        }

        return () => {
            if (scrollDisabled) {
                window.removeEventListener("resize", handler)
            }
        }
    }, [handleChangeResize, scrollDisabled])

    const wrapperContext = useModalWrapperContext()

  const topBorderRef = useRef<HTMLDivElement | null>(null)
  const autoScrolled = useRef(false)

    useLayoutEffect(() => {
        const property = "--scroll_padding"
        const propertyValue = document.documentElement.style.getPropertyValue(property)
        const {overflow} = document.body.style

        if (scrollDisabled && overflow !== "hidden") {
            if (document.body.scrollHeight > document.body.clientHeight && overflow !== "hidden") {
                document.documentElement.style.setProperty(property, "17px")
            }
            document.body.style.overflow = "hidden"
            window.scrollTo(scrollInfo.scrollLeft, scrollInfo.scrollTop)
            autoScrolled.current = true
        }

        return () => {
            const handleClose = () => {
                if (overflow !== "hidden") {
                    document.body.style.overflow = overflow
                    document.documentElement.style.setProperty(property, propertyValue)
                    window.scrollTo(scrollInfo.scrollLeft, scrollInfo.scrollTop)
                }
            }

            if (wrapperContext.wrapped) setTimeout(handleClose, 400)
            else handleClose()
        }
    }, [scrollDisabled])

  useLayoutEffect(() => {
    if (!autoScrolled.current && topBorderRef.current) topBorderRef.current.scrollIntoView()
  }, [topBorderRef.current])

    const modalContainerSettings = useMemo(
        () => ({
            center,
            fullScreen,
            noBottomPadding,
            containerClassName,
            ["data-cy"]: props["data-cy"],
            ["data-test"]: props["data-test"],
            ["data-testOpen"]: dataTestOpen,
            modalId: id,
            right,
        }),
        [center, fullScreen, noBottomPadding, containerClassName, props, id, dataTestOpen, right]
    )

    const closeIconClassName = classNames(styles["modal-close-button"], closeIconClass, {
        [styles["-fullscreen"]]: fullScreen,
        [styles["-bottom"]]: bottom,
    })

    useEffect(() => {
        //customWrapperStyles is usually not memoized
        if (id && customWrapperStyles) {
            //wait for animation
            setTimeout(() => {
                const modalNode = document.getElementById(id)
                if (modalNode) {
                    const wrapper = modalNode.parentElement?.parentElement
                    if (wrapper) {
                        for (const key of Object.keys(customWrapperStyles)) {
                            //@ts-ignore
                            wrapper.style[key] = customWrapperStyles[key]
                        }
                    }
                }
            }, 0)
        }
    }, [])

    const leftAnimation = animation === "left" || (!animation && !right && !fullScreen)
    const rightAnimation = animation === "right" || (!animation && right)
    const opacityAnimation = animation === "opacity" || (!animation && (fullScreen || center))

    const handleScrollbarsMount: LegacyRef<CustomScrollbars> = useCallback((scrollbars) => {
        if (scrollbars) {
            scrollViewRef.current = scrollbars.view
        }
    }, [])

    const useStickyHeader = props.useStickyHeader ?? false
    const stickyHeaderContainer = useRef<HTMLDivElement | null>(null)
    const StickyPortal = useMemo(() => {
        return PortalBuilder(stickyHeaderContainer.current)
    }, [stickyHeaderContainer.current])

    const sentinelRef = useRef<HTMLDivElement | null>(null)
    const [isSticky, setIsSticky] = useState(false)
    const [isStickyObserver] = useState<IntersectionObserver>(
        () =>
            new IntersectionObserver(([e]) => {
                setIsSticky(e.intersectionRatio === 0)
            })
    )

    useEffect(() => {
        if (sentinelRef.current) {
            isStickyObserver.observe(sentinelRef.current)
        }
        return () => {
            if (sentinelRef.current) isStickyObserver.unobserve(sentinelRef.current)
        }
    }, [sentinelRef.current])

    return (
        <RRM
            modalId={id}
            focusTrapped={focusTrapped}
            open={!!open}
            onClose={onClose}
            animationDuration={400}
            onAnimationEnd={() => setDataTestOpen(true)}
            classNames={{
                root: classNames(styles.root, rootClassName, {
                    [styles.withoutHeader]: withoutHeader,
                    [styles.fullScreen]: fullScreen,
                }),
                modalContainer: styles.modalContainer,
                modalAnimationIn: leftAnimation
                    ? styles.modalShiftLeft
                    : rightAnimation
                      ? styles.modalShiftRight
                      : opacityAnimation
                        ? styles.modalOpacity
                        : undefined,
                overlay: classNames(styles.overlay, overlayClassName, {
                    [styles.noBg]: noBackground,
                    [styles.fullScreen]: fullScreen,
                    [styles.darkTheme]: darkTheme,
                    [styles.withoutHeader]: withoutHeader,
                }),
                modal: classNames(styles.modal, className, {
                    [styles.right]: right,
                    [styles["-wide"]]: wide,
                    [styles.ultraWide]: ultraWide,
                    [styles.bottom]: bottom,
                    [styles.noShadow]: noShadow,
                    [styles.fullScreen]: fullScreen,
                    [styles.center]: center,
                    [styles.withoutHeader]: withoutHeader,
                    [styles.darkTheme]: darkTheme,
                    [styles.ownScroll]: ownScroll,
                    [styles.focusTrapped]: focusTrapped,

                    [styles.open]: open,
                    [styles.leftAnimation]: leftAnimation,
                    [styles.rightAnimation]: rightAnimation,
                    [styles.opacityAnimation]: opacityAnimation,
                }),
            }}
            blockScroll={false}
            closeOnOverlayClick={false}
            closeOnEsc={closeOnEsc}
            showCloseIcon={false}
        >
            {fullScreen || noScrollWrapper ? (
                <ModalContainer {...modalContainerSettings}>
                    {children}
                    <CloseIcon
                        isSmallIcon={false}
                        className={closeIconClassName}
                        onClick={onClose}
                        smooth={smoothCloseIcon}
                        show={showCloseIcon}
                        customCloseIcon={customCloseIcon}
                    />
                </ModalContainer>
            ) : useStickyHeader ? (
                <Scrollbars
                    style={{height: "100%"}}
                    scrollRef={handleScrollbarsMount}
                    autoHeightMin={"100%"}
                    autoHeightMax={"100%"}
                    autoHeight
                >
                    <div ref={sentinelRef} className={styles.sentinel} />
                    <div
                        ref={stickyHeaderContainer}
                        className={classNames(
                            styles.stickyContainer,
                            isSticky && styles.activeSticky,
                            showCloseIcon && styles.flex
                        )}
                    >
                        {showCloseIcon && (
                            <CloseIcon
                                className={styles.stickyCloseIcon}
                                onClick={onClose}
                                show
                                customCloseIcon={customCloseIcon}
                            />
                        )}
                    </div>
                    <DisableFormElementsComponent
                        disabled={disableFormElements}
                        className={classNames(styles.fieldset, styles.modalFormDisabler)}
                    >
                        <div className={styles.viewBorderTop} ref={topBorderRef}/>
                        <ModalContainer {...modalContainerSettings}>
                            <ModalScrollViewContext.Provider value={scrollViewRef.current}>
                                <ModalTopStickyContext.Provider value={StickyPortal}>
                                    {children}
                                </ModalTopStickyContext.Provider>
                            </ModalScrollViewContext.Provider>
                        </ModalContainer>
                    </DisableFormElementsComponent>
                </Scrollbars>
            ) : (
                <Scrollbars
                    style={{height: "100%"}}
                    scrollRef={handleScrollbarsMount}
                    autoHeightMin={"100%"}
                    autoHeightMax={"100%"}
                    autoHeight
                >
                    <DisableFormElementsComponent
                        disabled={disableFormElements}
                        className={classNames(styles.fieldset, styles.modalFormDisabler)}
                    >
                        <div className={styles.viewBorderTop} ref={topBorderRef} />
                        <ModalContainer {...modalContainerSettings}>
                            <ModalScrollViewContext.Provider value={scrollViewRef.current}>
                                {children}
                                <CloseIcon
                                    className={closeIconClassName}
                                    onClick={onClose}
                                    smooth={smoothCloseIcon}
                                    show={showCloseIcon}
                                    customCloseIcon={customCloseIcon}
                                />
                            </ModalScrollViewContext.Provider>
                        </ModalContainer>
                    </DisableFormElementsComponent>
                </Scrollbars>
            )}
        </RRM>
    )
}

interface ModalContainerProps {
    fullScreen?: boolean
    center?: boolean
    right?: boolean
    noBottomPadding?: boolean
    "data-cy"?: string
    "data-test"?: string
    "data-testOpen"?: boolean
    containerClassName?: string
    children?: ReactNode
    modalId?: string
}

export const ModalContainer: React.FC<ModalContainerProps> = memo(
    ({
        children,
        fullScreen,
        noBottomPadding,
        center,
        containerClassName,
        modalId = "",
        right,
        ...props
    }) => {
        const [loaderBox, setLoaderBox] = useState<HTMLDivElement | null>(null)
        const modalPosition = useMemo(() => ({id: modalId, right: !!right}), [right, modalId])

        return (
            <ModalLoaderContext.Provider value={loaderBox}>
                <ModalPositionContext.Provider value={modalPosition}>
                    <ContainerLayout
                        noPadding={fullScreen || center}
                        noBottomPadding={noBottomPadding}
                        data-cy={props["data-cy"] || "modal"}
                        data-test={props["data-test"]}
                        data-testOpen={!!props["data-testOpen"]}
                        withScroll={false}
                        className={classNames(styles.modalWrapper, containerClassName)}
                    >
                        {children}
                        {modalId && <div ref={setLoaderBox} />}
                    </ContainerLayout>
                </ModalPositionContext.Provider>
            </ModalLoaderContext.Provider>
        )
    }
)
